/* eslint-disable jsx-a11y/anchor-is-valid */
import { Fragment, useContext, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Bars3Icon, LinkIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getNavigationRoutes } from "../../../config/router";
import { Link } from "react-router-dom";
import { classNames, isCurrentPath } from "../../../utils";
import config from "../landing.config";
import { useFetchSponsors } from "../../../api/hooks";
import SponsorsBar from "../components/SponsorsBar";
import { useAptabase } from "@aptabase/react";
import { RootContext } from "../../../providers/RootProvider";

const LandingPage = () => {
  const { featureFlags } = useContext(RootContext);
  const isTournamentReady = Boolean(featureFlags?.["tournament_ready"]);
  const routes = getNavigationRoutes(
    isTournamentReady,
    featureFlags?.["enable_stats"] ?? false
  );
  const { data } = useFetchSponsors();
  const { trackEvent } = useAptabase();

  useEffect(() => {
    trackEvent("page_view", { screenName: "landing_page" });
  }, [trackEvent]);

  // const onClickLive = () => {
  //   trackEvent("click", { screenName: "landing_page", buttonName: "live" });
  // };

  const onClickDiscover = () => {
    trackEvent("click", {
      screenName: "landing_page",
      buttonName: "discover",
    });
  };

  const onClickPreviousEdition = () => {
    trackEvent("click", {
      screenName: "landing_page",
      buttonName: "previous_edition",
    });
  };

  return (
    <>
      <div className="relative overflow-hidden bg-white">
        <div className="mx-auto max-w-7xl">
          <div className="relative z-10 bg-white pb-8 sm:pb-8 md:pb-12 lg:w-full lg:max-w-2xl lg:pb-16 xl:pb-10">
            <svg
              className="absolute inset-y-0 right-0 hidden h-full w-48 translate-x-1/2 transform text-white lg:block"
              fill="currentColor"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <polygon points="50,0 100,0 50,100 0,100" />
            </svg>

            <Popover>
              <div className="relative px-4 pt-6 sm:px-6 lg:px-8">
                <nav
                  className="relative flex items-center justify-between sm:h-10 lg:justify-start"
                  aria-label="Global"
                >
                  <div className="flex flex-shrink-0 flex-grow items-center lg:flex-grow-0">
                    <div className="flex w-full items-center justify-between md:w-auto">
                      <Link to={"/"}>
                        <span className="sr-only">{config.navTitle}</span>
                        <img
                          alt={config.logo.altName}
                          className="h-15 w-auto sm:h-20"
                          src={config.logo.src}
                        />
                      </Link>
                      <div className="-mr-2 flex items-center md:hidden">
                        <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500">
                          <span className="sr-only">Open main menu</span>
                          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                        </Popover.Button>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:ml-10 md:block md:space-x-8 md:pr-4">
                    {routes.map((item) => (
                      <Link
                        key={item.title}
                        to={item.path}
                        className={classNames(
                          "font-medium text-gray-500 hover:text-gray-900",
                          isCurrentPath(item.path)
                            ? "border-red-500 text-gray-900"
                            : ""
                        )}
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </nav>
              </div>

              <Transition
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="duration-100 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Popover.Panel
                  focus
                  className="absolute inset-x-0 top-0 z-10 origin-top-right transform p-2 transition md:hidden"
                >
                  <div className="overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black ring-opacity-5">
                    <div className="flex items-center justify-between px-5 pt-4">
                      <div>
                        <img className="h-8 w-auto" src="/logo512.png" alt="" />
                      </div>
                      <div className="-mr-2">
                        <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500">
                          <span className="sr-only">Close main menu</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </Popover.Button>
                      </div>
                    </div>
                    <div className="space-y-1 px-2 pt-2 pb-3">
                      {routes.map((item) => (
                        <Link
                          key={item.title}
                          to={item.path}
                          className={classNames(
                            "block rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                            isCurrentPath(item.path)
                              ? "border-red-500 text-gray-900"
                              : ""
                          )}
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </Popover>
            <main className="mx-auto mt-10 max-w-7xl px-4 sm:mt-4 sm:px-6 md:mt-8 lg:mt-12 lg:px-8 xl:mt-15">
              <div className="sm:text-center lg:text-left">
                <h1
                  className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl pb-4"
                  style={{ lineHeight: "0.9" }}
                >
                  <span className="block xl:inline">
                    {config.titlePart1}
                    {"\n"}
                    <span className="text-red-400">{config.titlePart2}</span>
                  </span>
                </h1>
                <span className="mt-3 text-2xl font-medium text-red-800 sm:mx-auto sm:mt-5 sm:max-w-xl sm:text-2xl md:mt-5 md:text-2xl lg:mx-0">
                  {config.date}
                </span>
                <p className="mt-3 text-base text-gray-500 sm:mx-auto sm:mt-5 sm:max-w-xl sm:text-lg md:mt-5 md:text-xl lg:mx-0">
                  {config.body}
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <div>
                    <a
                      href={config.mainButton.link}
                      className="flex w-full items-center justify-center rounded-md border border-transparent px-8 py-3 text-base font-medium md:py-4 md:px-10 md:text-lg bg-red-600 text-white hover:bg-red-700"
                      target={
                        config.mainButton.link.startsWith("http")
                          ? "_blank"
                          : undefined
                      }
                      rel="noreferrer"
                    >
                      {config.mainButton.label}
                    </a>
                  </div>
                  {config.secondaryButton.link &&
                  config.secondaryButton.label ? (
                    <div className="mt-3 sm:mt-0 sm:ml-3 rounded-md shadow">
                      <a
                        href={config.secondaryButton.link}
                        target={
                          config.secondaryButton.link.startsWith("http")
                            ? "_blank"
                            : undefined
                        }
                        rel="noreferrer"
                        className="flex w-full items-center justify-center rounded-md border border-transparent px-8 py-3 text-base font-medium md:py-4 md:px-10 md:text-lg bg-red-100 text-red-700 hover:bg-red-200"
                        onClick={onClickDiscover}
                      >
                        {config.secondaryButton.label}
                      </a>
                    </div>
                  ) : null}
                </div>
                {config.altLink?.label && config.altLink?.link ? (
                  <div className="flex text-sm pt-3">
                    <a
                      href={config.altLink.link}
                      target={
                        config.altLink.link.startsWith("http")
                          ? "_blank"
                          : undefined
                      }
                      className="group inline-flex items-center font-medium text-red-600 hover:text-red-900"
                      rel="noreferrer"
                      onClick={onClickPreviousEdition}
                    >
                      <LinkIcon
                        className="h-5 w-5 text-red-500 group-hover:text-red-900"
                        aria-hidden="true"
                      />
                      <span className="ml-2">{config.altLink.label}</span>
                    </a>
                  </div>
                ) : null}
              </div>
              <a href="https://mycontest.club" target="_blank" rel="noreferrer">
                <div className="bg-gray-50 py-4 mt-2">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center space-y-2">
                      <h3 className="text-base font-semibold text-red-600 tracking-wide">
                        Sponsorisé par
                      </h3>
                      <div className="flex justify-center items-center">
                        <img
                          src="/assets/mycontest-club.png"
                          alt="mycontest.club"
                          className="h-12 object-contain"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500 mb-4 w-50%">
                        Organisateur de tournois sur-mesure
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <video
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:h-full lg:w-full"
            src="/assets/landing-page-hero.mp4"
            controls={false}
            autoPlay
            loop
            muted
            playsInline
          />
        </div>
      </div>
      {data && data?.length > 0 ? <SponsorsBar sponsors={data} /> : null}
    </>
  );
};

export default LandingPage;
